import { Injectable, SecurityContext } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable } from 'rxjs';
import { OperationResult } from '../../models/server-result/operation-result';
import { LogService } from '../../services/log/log.service';
import { UtilitiesService } from '../../services/utils/utilities.service';
import { VerticalBarChart } from '../../models/charts/vertical-bar-chart';
import { TranslateService } from '../../services/translate/translate.service';
import { CustomTabItem, TabAction } from '../../shared-components/custom-tabs/custom-tabs.component';
import { SegmentedControlOption } from '../../shared-components/custom-segmented-control/custom-segmented-control.component';
import { DocumentForecastByState } from '../../models/dashboard/document-forecast-by-paymentstate';
import { CurrencyViewModel, DASHBOARD_TAB_CONFIRMING, DASHBOARD_TAB_FACTORING, SeriesData } from './dashboard.types';
import { DocumentPaymentState } from '../../models/enums/document-payment-state';
import {
	DASHBOARD_COLOR_NAMES,
	DOCUMENT_FORECAST_FALLBACK_COLOR,
	DOCUMENT_FORECAST_STATE_COLORS,
	resolveDashboardColor,
} from './dashboard-chart.config';

@Injectable()
export class DashboardHelperService {
	constructor(
		private logger: LogService,
		private domSanitizer: DomSanitizer,
		private utilitiesService: UtilitiesService,
		private translateService: TranslateService
	) {}

	public loadMetric<T>(
		source: Observable<OperationResult<T>>,
		setLoading: (loading: boolean) => void,
		setValue: (value: T) => void,
		invalidResultLogLabel: string,
		httpErrorLogLabel?: string
	): void {
		setLoading(true);
		source.subscribe({
			next: (opResult: OperationResult<T>) => {
				setLoading(false);
				opResult.IsValid ? setValue(opResult.Data) : this.logger.error(invalidResultLogLabel, opResult);
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				setLoading(false);
				this.logger.error(httpErrorLogLabel ?? invalidResultLogLabel, this.domSanitizer.sanitize(SecurityContext.HTML, httpErrorResponse));
			},
		});
	}

	public contractsAvailableRatio(availableAmount: number, contractedAmount: number): number {
		return contractedAmount > 0 ? 100.0 * (availableAmount / contractedAmount) : 0;
	}

	public distinctCount(values: (string | null | undefined)[]): number {
		return this.utilitiesService.getDistinctValues(values.filter(this.utilitiesService.notEmpty)).length;
	}

	public sortCurrencies(currencies: CurrencyViewModel[]): CurrencyViewModel[] {
		return currencies.sort(function (c1: CurrencyViewModel, c2: CurrencyViewModel) {
			var currency1 = c1.Currency.toUpperCase();
			var currency2 = c2.Currency.toUpperCase();
			return currency1 < currency2 ? -1 : currency1 > currency2 ? 1 : 0;
		});
	}

	public addCurrencyIfMissing(currencies: CurrencyViewModel[], currency: string): void {
		if (currencies.findIndex((c) => c.Currency == currency) == -1)
			currencies.push(<CurrencyViewModel>{
				Currency: currency,
			});
	}

	public toCurrencyOptions(currencies: CurrencyViewModel[]): SegmentedControlOption[] {
		return currencies.map((currency) => ({ value: currency.Currency, label: currency.Currency }));
	}

	public createTabs(confirmingVisible: boolean, factoringVisible: boolean): CustomTabItem[] {
		return [
			{
				id: DASHBOARD_TAB_CONFIRMING,
				label: this.translateService.translate('menu.Confirming'),
				condition: confirmingVisible,
			},
			{
				id: DASHBOARD_TAB_FACTORING,
				label: this.translateService.translate('menu.Factoring'),
				condition: factoringVisible,
			},
		];
	}

	public createRefreshAction(onRefresh: () => void): TabAction {
		return {
			type: 'button',
			text: this.translateService.translate('button.refresh'),
			preIcon: 'fa-solid fa-sync-alt',
			action: onRefresh,
		};
	}

	public buildDocumentForecastLineSeries(data: DocumentForecastByState[], formatDate: (date: Date) => string): SeriesData[] {
		const dates: string[] = [];
		const states: string[] = [];
		const amounts = new Map<string, number>();

		data.forEach((element) => {
			const date = formatDate(element.BuyerPaymentDate);
			if (!dates.includes(date)) dates.push(date);

			const state = this.documentForecastStateLabel(element.DocumentPaymentState);
			if (!state) return;

			if (!states.includes(state)) states.push(state);
			const key = `${state}|${date}`;
			amounts.set(key, (amounts.get(key) ?? 0) + Number(element.TotalAmount));
		});

		if (states.length === 0 && dates.length > 0) {
			return [{ name: '', series: dates.map((date) => ({ name: date, value: 0 })) }];
		}

		return states.map((state) => ({
			name: state,
			series: dates.map((date) => ({ name: date, value: amounts.get(`${state}|${date}`) ?? 0 })),
		}));
	}

	public buildDocumentForecastCustomColors(data: DocumentForecastByState[]): { name: string; value: string }[] {
		const colors = new Map<string, string>();

		data.forEach((element) => {
			const state = this.documentForecastStateLabel(element.DocumentPaymentState);
			if (!state || colors.has(state)) return;

			colors.set(state, this.documentForecastStateColor(element.DocumentPaymentState));
		});

		return [...colors.entries()].map(([name, value]) => ({ name, value }));
	}

	private documentForecastStateLabel(state: string | null | undefined): string | null {
		return state ? this.translateService.translate(state, 'list-paymentState') : null;
	}

	private documentForecastStateColor(state: string): string {
		return state in DOCUMENT_FORECAST_STATE_COLORS
			? resolveDashboardColor(DOCUMENT_FORECAST_STATE_COLORS[state as DocumentPaymentState])
			: DOCUMENT_FORECAST_FALLBACK_COLOR;
	}

	public createVerticalBarChart(xAxisLabel: string, yAxisLabel: string): VerticalBarChart {
		const chart = new VerticalBarChart();
		chart.View = [];
		chart.ShowXAxis = true;
		chart.ShowLegend = false;
		chart.LegendTitle = '';
		chart.ShowYAxis = true;
		chart.ShowXAxisLabel = true;
		chart.XAxisLabel = xAxisLabel;
		chart.ShowYAxisLabel = true;
		chart.YAxisLabel = yAxisLabel;
		chart.ColorScheme = {
			domain: DASHBOARD_COLOR_NAMES.map(resolveDashboardColor),
		};
		return chart;
	}
}
